/**
 * Add Cattle Page - Production-level cattle registration form
 * Updated to use master data IDs and proper field mappings for backend integration
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save, ArrowLeft, Scan, Upload, Camera, FileText,
  User, Activity, Baby, Globe, Settings, Ruler,
  AlertCircle, CheckCircle, Clock, Plus, X, WifiOff
} from 'lucide-react';
import {
  gauShalaApi,
  calculateDobFromAge,
  type Breed,
  type Species,
  type Gender,
  type Color,
  type Cattle,
  type Source,
  type Ownership,
  type FeedType
} from '../../services/gaushala/api';

import { InputField, SelectField, TextAreaField, FileUploadField, MultiSelectField } from './CattleFormFields';
import { useOffline } from '../../contexts/OfflineContext';
import { useOfflineMasterData } from '../../hooks/useOfflineMasterData';
import { offlineCattleApi, generateTempId, isTempId } from '../../lib/offline';
interface LanguageContextType {
  language: 'hi' | 'en';
  t: (key: string) => string;
}

interface AddCattleProps {
  languageContext: LanguageContextType;
}

export default function AddCattle({ languageContext }: AddCattleProps) {
  const navigate = useNavigate();

  // Offline support hooks
  const { isOnline, pendingSyncCount, triggerSync } = useOffline();
  const {
    breeds: offlineBreeds,
    species: offlineSpecies,
    genders: offlineGenders,
    colors: offlineColors,
    sources: offlineSources,
    ownership: offlineOwnerships,
    feedTypes: offlineFeedTypes,
    loading: offlineMasterDataLoading,
    isOffline: masterDataOffline,
  } = useOfflineMasterData();

  // Validation constraints
  const VALIDATION_RULES = {
    uniqueAnimalId: { max: 20 },
    name: { max: 30 },
    rfidTagNo: { max: 20 },
    earTagNumber: { max: 15 },
    microchipNumber: { max: 20 },
    vaccinationStatus: { max: 100 },
    veterinarianName: { max: 50 },
    veterinarianContact: { max: 10 },
    dewormingSchedule: { max: 100 },
    medicalHistory: { max: 500 },
    previousOwner: { max: 50 },
    feedingSchedule: { max: 300 },
    weight: { min: 0, max: 2000, maxLength: 6 }, // Max 2000.0 kg (6 chars including decimal)
    height: { min: 0, max: 300, maxLength: 3 }   // Max 300 cm (3 chars, whole numbers only)
  };

  // Master data state
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [genders, setGenders] = useState<Gender[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [ownerships, setOwnerships] = useState<Ownership[]>([]);
  const [feedTypes, setFeedTypes] = useState<FeedType[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [sheds, setSheds] = useState<any[]>([]);
  const [masterDataLoading, setMasterDataLoading] = useState(true);

  const [formData, setFormData] = useState({
    // Basic Identification - Updated to use IDs
    uniqueAnimalId: '',
    name: '',
    breedId: 0,
    speciesId: 0,
    genderId: 0,
    colorId: 0,
    ageYears: '',  // User enters age, will be converted to dob on submit
    dateOfEntry: new Date().toISOString().split('T')[0],  // Store as date string for form input


    // Health & Medical Records
    vaccinationStatus: '',
    disabilityStatus: '',
    veterinarianName: '',
    dewormingSchedule: '',
    lastHealthCheckup: '',
    veterinarianContact: '',
    medicalHistory: '',

    // Physical Characteristics - Updated field name
    weight: '',
    hornStatus: '',
    rfidTagNo: '',  // Changed from rfidTagNumber to match backend
    height: '',
    earTagNumber: '',
    microchipNumber: '',

    // Reproductive Details (Female only)
    milkingStatus: '',
    milkYieldPerDay: '',
    numberOfCalves: '',
    lactationNumber: '',
    lastCalvingDate: '',
    pregnancyStatus: '',

    // Origin & Ownership
    sourceOfAcquisition: '',
    previousOwner: '',
    dateOfAcquisition: '',
    ownershipStatus: '',

    // Shelter & Feeding
    shedNumber: [],
    feedTypeIds: [] as number[], // Changed from single typeOfFeed string to array of IDs
    feedingSchedule: '',

    // Supporting Documents
    photoFile: null as File | null,
    healthCertificate: null as File | null,
    vaccinationRecord: null as File | null,
    purchaseDocument: null as File | null
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState(0);

  // Duplicate check state for real-time validation
  const [duplicateCheckState, setDuplicateCheckState] = useState<{
    uniqueAnimalId: { checking: boolean; exists: boolean | null; };
    rfidTagNo: { checking: boolean; exists: boolean | null; };
  }>({
    uniqueAnimalId: { checking: false, exists: null },
    rfidTagNo: { checking: false, exists: null },
  });

  // Debounce timers for duplicate checks
  const uniqueAnimalIdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rfidTagNoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load master data on component mount - with offline fallback
  useEffect(() => {
    const loadMasterData = async () => {
      setMasterDataLoading(true);
      try {
        // Try online API first
        if (isOnline) {
          const [breedsRes, speciesRes, gendersRes, colorsRes, shedsRes, sourcesRes, ownershipsRes, feedTypesRes] = await Promise.all([
            gauShalaApi.masterData.getAllBreeds(),
            gauShalaApi.masterData.getAllSpecies(),
            gauShalaApi.masterData.getAllGenders(),
            gauShalaApi.masterData.getAllColors(),
            gauShalaApi.sheds.getAvailableSheds(),
            gauShalaApi.masterData.getAllSources(),
            gauShalaApi.masterData.getAllOwnership(),
            gauShalaApi.masterData.getAllFeedTypes(),
          ]);

          // Check if API calls succeeded - if not, use offline cache
          const apiSuccess = breedsRes.success && speciesRes.success && gendersRes.success;

          if (apiSuccess) {
            if (breedsRes.data) setBreeds(breedsRes.data);
            if (speciesRes.data) setSpecies(speciesRes.data);
            if (gendersRes.data) setGenders(gendersRes.data);
            if (colorsRes.data) setColors(colorsRes.data);
            if (shedsRes.success && shedsRes.data) setSheds(shedsRes.data);
            if (sourcesRes.data) setSources(sourcesRes.data);
            if (ownershipsRes.data) setOwnerships(ownershipsRes.data);
            if (feedTypesRes.data) setFeedTypes(feedTypesRes.data);

            // Debug log for ownership data
            console.log('🔵 [ADD CATTLE] Master data loaded from server:', {
              breeds: breedsRes.data?.length || 0,
              species: speciesRes.data?.length || 0,
              genders: gendersRes.data?.length || 0,
              colors: colorsRes.data?.length || 0,
              sheds: shedsRes.data?.length || 0,
              sources: sourcesRes.data?.length || 0,
              ownerships: ownershipsRes.data?.length || 0,
              feedTypes: feedTypesRes.data?.length || 0
            });
          } else {
            // API returned errors - fall back to offline cache
            console.log('⚠️ [ADD CATTLE] API failed - falling back to cached data');
            if (offlineBreeds.length > 0) {
              setBreeds(offlineBreeds);
              setSpecies(offlineSpecies);
              setGenders(offlineGenders);
              setColors(offlineColors);
              setSources(offlineSources);
              setOwnerships(offlineOwnerships);
              setFeedTypes(offlineFeedTypes);
              // Try to get sheds, fall back to empty
              if (shedsRes.success && shedsRes.data) {
                setSheds(shedsRes.data);
              } else {
                setSheds([]);
              }
              setMessage({
                type: 'warning',
                text: 'Server unavailable. Using cached data - some features may be limited.'
              });
              console.log('📴 [ADD CATTLE] Using cached master data:', {
                breeds: offlineBreeds.length,
                species: offlineSpecies.length,
                genders: offlineGenders.length,
                colors: offlineColors.length,
                sources: offlineSources.length,
                ownerships: offlineOwnerships.length,
                feedTypes: offlineFeedTypes.length
              });
            } else {
              setMessage({
                type: 'error',
                text: 'Server unavailable and no cached data. Please try again later.'
              });
            }
          }
        } else {
          // Offline mode - use cached master data
          console.log('📴 [ADD CATTLE] Offline - using cached master data');
          setBreeds(offlineBreeds);
          setSpecies(offlineSpecies);
          setGenders(offlineGenders);
          setColors(offlineColors);
          setSources(offlineSources);
          setOwnerships(offlineOwnerships);
          setFeedTypes(offlineFeedTypes);
          // Sheds from offline cache
          try {
            const shedsRes = await gauShalaApi.sheds.getAvailableSheds();
            if (shedsRes.success && shedsRes.data) setSheds(shedsRes.data);
          } catch {
            console.log('📴 [ADD CATTLE] Could not load sheds - using empty array');
            setSheds([]);
          }
        }

        // Clear error message when data loads successfully
        setMessage(null);
      } catch (error) {
        console.error('Failed to load master data:', error);
        // Fall back to offline data if server fails
        if (offlineBreeds.length > 0) {
          console.log('📴 [ADD CATTLE] Server failed - falling back to cached data');
          setBreeds(offlineBreeds);
          setSpecies(offlineSpecies);
          setGenders(offlineGenders);
          setColors(offlineColors);
          setSources(offlineSources);
          setOwnerships(offlineOwnerships);
          setFeedTypes(offlineFeedTypes);
          setMessage({
            type: 'warning',
            text: 'Using cached reference data. Some options may be outdated.'
          });
        } else {
          setMessage({
            type: 'error',
            text: 'Failed to load reference data. Please connect to the internet and refresh.'
          });
        }
      } finally {
        setMasterDataLoading(false);
      }
    };

    loadMasterData();
  }, [isOnline, offlineBreeds, offlineSpecies, offlineGenders, offlineColors, offlineSources, offlineOwnerships, offlineFeedTypes]);

  const handleInputChange = useCallback((field: string, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field - use functional update to prevent input unfocus bug
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
    setMessage(null);
  }, []); // Empty dependency array - function is now stable

  const handleScanRfid = async () => {
    setIsScanning(true);
    try {
      const response = await gauShalaApi.cattle.scanRfid();
      if (response.success && response.data) {
        handleInputChange('rfidTagNo', response.data.rfidTag);

        if (response.data.cattleInfo) {
          setMessage({
            type: 'warning',
            text: 'RFID tag already exists for another cattle'
          });
        } else {
          setMessage({
            type: 'success',
            text: 'RFID tag scanned successfully'
          });
        }
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to scan RFID tag'
      });
    } finally {
      setIsScanning(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields validation - Updated for ID fields
    if (!formData.uniqueAnimalId.trim()) newErrors.uniqueAnimalId = 'Unique Animal ID is required';
    if (!formData.name.trim()) newErrors.name = 'Cattle Name is required';
    if (formData.breedId === 0) newErrors.breedId = 'Breed is required';
    if (formData.speciesId === 0) newErrors.speciesId = 'Species is required';
    if (formData.genderId === 0) newErrors.genderId = 'Gender is required';
    // Age validation
    if (!formData.ageYears || formData.ageYears === '') {
      newErrors.ageYears = 'Please enter the age';
    } else {
      const ageVal = parseInt(formData.ageYears);
      if (isNaN(ageVal) || ageVal < 0 || ageVal > 30) {
        newErrors.ageYears = 'Age must be between 0 and 30 years';
      }
    }
    // Date of Entry is automatically set to current date, no validation needed
    if (!formData.rfidTagNo.trim()) newErrors.rfidTagNo = 'RFID Tag Number is required';
    if (!formData.disabilityStatus) newErrors.disabilityStatus = 'Health Status is required';

    // Check if duplicates exist from real-time validation
    if (duplicateCheckState.uniqueAnimalId.exists) {
      newErrors.uniqueAnimalId = 'This Unique Animal ID already exists';
    }
    if (duplicateCheckState.rfidTagNo.exists) {
      newErrors.rfidTagNo = 'This RFID Tag already exists';
    }

    // Validate weight and height (optional fields, but if provided must be valid)
    if (formData.weight) {
      const weightValue = parseFloat(formData.weight);
      if (isNaN(weightValue)) {
        newErrors.weight = 'Weight must be a valid number';
      } else if (weightValue < VALIDATION_RULES.weight.min || weightValue > VALIDATION_RULES.weight.max) {
        newErrors.weight = `Weight must be between ${VALIDATION_RULES.weight.min} and ${VALIDATION_RULES.weight.max} kg`;
      }
    }

    if (formData.height) {
      const heightValue = parseFloat(formData.height);
      if (isNaN(heightValue)) {
        newErrors.height = 'Height must be a valid number';
      } else if (heightValue < VALIDATION_RULES.height.min || heightValue > VALIDATION_RULES.height.max) {
        newErrors.height = `Height must be between ${VALIDATION_RULES.height.min} and ${VALIDATION_RULES.height.max} cm`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time duplicate check for Unique Animal ID
  const checkUniqueAnimalIdAvailability = useCallback(async (id: string) => {
    if (!id || id.trim().length < 3) {
      setDuplicateCheckState(prev => ({
        ...prev,
        uniqueAnimalId: { checking: false, exists: null }
      }));
      return;
    }

    setDuplicateCheckState(prev => ({
      ...prev,
      uniqueAnimalId: { checking: true, exists: null }
    }));

    try {
      const response = await gauShalaApi.cattle.checkUniqueAnimalId(id.trim());
      if (response.success && response.data) {
        setDuplicateCheckState(prev => ({
          ...prev,
          uniqueAnimalId: { checking: false, exists: response.data!.exists }
        }));

        // Set error if duplicate exists
        if (response.data.exists) {
          setErrors(prev => ({
            ...prev,
            uniqueAnimalId: 'This Unique Animal ID already exists'
          }));
        } else {
          setErrors(prev => {
            const { uniqueAnimalId, ...rest } = prev;
            return rest;
          });
        }
      } else {
        // API failed (server down or error response) - reset checking state
        // In offline mode, we'll allow the save and sync later
        console.log('[ADD CATTLE] Unique ID check skipped - server unavailable');
        setDuplicateCheckState(prev => ({
          ...prev,
          uniqueAnimalId: { checking: false, exists: null }
        }));
      }
    } catch (error) {
      console.warn('Error checking unique animal ID availability:', error);
      setDuplicateCheckState(prev => ({
        ...prev,
        uniqueAnimalId: { checking: false, exists: null }
      }));
    }
  }, []);

  // Real-time duplicate check for RFID Tag
  const checkRfidTagAvailability = useCallback(async (rfid: string) => {
    if (!rfid || rfid.trim().length < 3) {
      setDuplicateCheckState(prev => ({
        ...prev,
        rfidTagNo: { checking: false, exists: null }
      }));
      return;
    }

    setDuplicateCheckState(prev => ({
      ...prev,
      rfidTagNo: { checking: true, exists: null }
    }));

    try {
      const response = await gauShalaApi.cattle.checkRfidTag(rfid.trim());
      if (response.success && response.data) {
        setDuplicateCheckState(prev => ({
          ...prev,
          rfidTagNo: { checking: false, exists: response.data!.exists }
        }));

        // Set error if duplicate exists
        if (response.data.exists) {
          setErrors(prev => ({
            ...prev,
            rfidTagNo: 'This RFID Tag already exists'
          }));
        } else {
          setErrors(prev => {
            const { rfidTagNo, ...rest } = prev;
            return rest;
          });
        }
      } else {
        // API failed (server down or error response) - reset checking state
        // In offline mode, we'll allow the save and sync later
        console.log('[ADD CATTLE] RFID check skipped - server unavailable');
        setDuplicateCheckState(prev => ({
          ...prev,
          rfidTagNo: { checking: false, exists: null }
        }));
      }
    } catch (error) {
      console.warn('Error checking RFID tag availability:', error);
      setDuplicateCheckState(prev => ({
        ...prev,
        rfidTagNo: { checking: false, exists: null }
      }));
    }
  }, []);

  // Debounced handler for Unique Animal ID changes
  const handleUniqueAnimalIdChange = useCallback((value: string) => {
    handleInputChange('uniqueAnimalId', value);

    // Clear existing timeout
    if (uniqueAnimalIdTimeoutRef.current) {
      clearTimeout(uniqueAnimalIdTimeoutRef.current);
    }

    // Set new timeout for debounced check (500ms delay)
    uniqueAnimalIdTimeoutRef.current = setTimeout(() => {
      checkUniqueAnimalIdAvailability(value);
    }, 500);
  }, [handleInputChange, checkUniqueAnimalIdAvailability]);

  // Debounced handler for RFID Tag changes
  const handleRfidTagChange = useCallback((value: string) => {
    handleInputChange('rfidTagNo', value);

    // Clear existing timeout
    if (rfidTagNoTimeoutRef.current) {
      clearTimeout(rfidTagNoTimeoutRef.current);
    }

    // Set new timeout for debounced check (500ms delay)
    rfidTagNoTimeoutRef.current = setTimeout(() => {
      checkRfidTagAvailability(value);
    }, 500);
  }, [handleInputChange, checkRfidTagAvailability]);

  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      if (uniqueAnimalIdTimeoutRef.current) {
        clearTimeout(uniqueAnimalIdTimeoutRef.current);
      }
      if (rfidTagNoTimeoutRef.current) {
        clearTimeout(rfidTagNoTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to convert date string (YYYY-MM-DD) to LocalDateTime format (YYYY-MM-DDTHH:MM:SS)
  const convertToLocalDateTime = (dateString: string): string | undefined => {
    if (!dateString || dateString.trim() === '') return undefined;
    // If already in ISO format, return as-is
    if (dateString.includes('T')) return dateString;
    // Convert date-only string to LocalDateTime at midnight
    return `${dateString}T00:00:00`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if we're still checking availability (only when online)
    if (isOnline && (duplicateCheckState.uniqueAnimalId.checking || duplicateCheckState.rfidTagNo.checking)) {
      setMessage({
        type: 'warning',
        text: 'Please wait for availability checks to complete'
      });
      return;
    }

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields and fix any errors'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // Transform formData to match Cattle interface for backend - COMPLETE FIELD MAPPING
      // NOTE: gaushalaId is NOT sent - backend extracts it from JWT token automatically
      const cattleData: Omit<Cattle, 'id' | 'createdAt' | 'updatedAt' | 'gaushalaId'> = {
        uniqueAnimalId: formData.uniqueAnimalId,
        name: formData.name.trim(),
        breedId: formData.breedId,
        speciesId: formData.speciesId,
        genderId: formData.genderId,
        colorId: formData.colorId,
        dob: formData.ageYears && parseInt(formData.ageYears) > 0 ? calculateDobFromAge(parseInt(formData.ageYears)) : new Date().toISOString(),

        // Physical Characteristics
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        height: formData.height ? parseFloat(formData.height) : undefined,
        hornStatus: formData.hornStatus || undefined,
        rfidTagNo: formData.rfidTagNo,
        earTagNo: formData.earTagNumber || undefined,
        microchipNo: formData.microchipNumber || undefined,
        shedNumber: Array.isArray(formData.shedNumber) && formData.shedNumber.length > 0
          ? formData.shedNumber[0]
          : (typeof formData.shedNumber === 'string' ? formData.shedNumber : undefined),

        // Health & Medical Records
        vaccinationStatus: formData.vaccinationStatus || undefined,
        disabilityStatus: formData.disabilityStatus || undefined,
        dewormingSchedule: formData.dewormingSchedule || undefined,
        medicalHistory: formData.medicalHistory || undefined,
        lastHealthCheckupDate: formData.lastHealthCheckup ? convertToLocalDateTime(formData.lastHealthCheckup) : undefined,
        vetName: formData.veterinarianName || undefined,
        vetContact: formData.veterinarianContact || undefined,

        // Reproductive Details
        milkingStatus: formData.milkingStatus || undefined,
        lactationNumber: formData.lactationNumber ? parseInt(formData.lactationNumber) : undefined,
        milkYieldPerDay: formData.milkYieldPerDay ? parseFloat(formData.milkYieldPerDay) : undefined,
        lastCalvingDate: formData.lastCalvingDate ? convertToLocalDateTime(formData.lastCalvingDate) : undefined,
        calvesCount: formData.numberOfCalves ? parseInt(formData.numberOfCalves) : undefined,
        pregnancyStatus: formData.pregnancyStatus || undefined,

        // Origin & Ownership
        sourceId: formData.sourceOfAcquisition ? parseInt(formData.sourceOfAcquisition) : undefined,
        dateOfAcquisition: formData.dateOfAcquisition ? convertToLocalDateTime(formData.dateOfAcquisition) : undefined,
        previousOwner: formData.previousOwner || undefined,
        ownershipId: formData.ownershipStatus ? parseInt(formData.ownershipStatus) : undefined,

        // Shelter & Feeding
        feedingSchedule: formData.feedingSchedule || undefined,
        feedTypeIds: formData.feedTypeIds && formData.feedTypeIds.length > 0 ? formData.feedTypeIds : undefined,

        // System fields
        dateOfEntry: formData.dateOfEntry ? convertToLocalDateTime(formData.dateOfEntry) : convertToLocalDateTime(new Date().toISOString().split('T')[0]),
        isActive: true,
        totalDungCollected: 0,
        lastDungCollection: 0,
      };

      // ============== OFFLINE MODE HANDLING ==============
      if (!isOnline) {
        // Warn about file uploads
        const hasFiles = formData.photoFile || formData.healthCertificate || formData.vaccinationRecord || formData.purchaseDocument;
        if (hasFiles) {
          console.log('📴 [ADD CATTLE] Offline mode - files will NOT be uploaded until back online');
        }

        console.log('📴 [ADD CATTLE] Queueing cattle creation for offline sync');

        // Use offline API to queue the creation
        const offlineResult = await offlineCattleApi.create(cattleData);

        if (offlineResult.success) {
          console.log('✅ [ADD CATTLE] Cattle queued for offline sync with temp ID:', offlineResult.tempId);
          setMessage({
            type: 'success',
            text: `Cattle saved locally! ${hasFiles ? 'Note: Documents will be uploaded when you reconnect.' : ''} Will sync when online.`
          });

          // Navigate after short delay
          setTimeout(() => {
            navigate('/gaushala/cattle', { state: { shouldRefresh: true, refreshSheds: true } });
          }, 2000);
          return;
        } else {
          throw new Error(offlineResult.error || 'Failed to save cattle offline');
        }
      }

      // ============== ONLINE MODE - ORIGINAL BEHAVIOR ==============
      // Pass all files (photo + documents) with cattle registration - saves everything in one request
      const documentFiles = {
        healthCertificate: formData.healthCertificate || undefined,
        vaccinationRecord: formData.vaccinationRecord || undefined,
        purchaseDocument: formData.purchaseDocument || undefined,
      };

      console.log('🔵 [ADD CATTLE] Registering cattle with all files:', {
        hasPhoto: !!formData.photoFile,
        hasHealthCertificate: !!documentFiles.healthCertificate,
        hasVaccinationRecord: !!documentFiles.vaccinationRecord,
        hasPurchaseDocument: !!documentFiles.purchaseDocument,
      });

      const response = await gauShalaApi.cattle.createCattle(
        cattleData,
        formData.photoFile || undefined,
        documentFiles
      );

      console.log('🔵 [ADD CATTLE] Create response:', response);
      console.log('🔵 [ADD CATTLE] Cattle ID:', response.data?.id);

      if (response.success && response.data && response.data.id) {
        const cattleId = String(response.data.id);
        console.log('✅ [ADD CATTLE] Cattle registered successfully with ID:', cattleId);
        setMessage({
          type: 'success',
          text: 'Cattle registered successfully!'
        });

        // Wait a bit to ensure backend has processed all uploads before navigating
        // This ensures the photo and other documents are available when viewing the cattle
        setTimeout(() => {
          console.log('🔵 [ADD CATTLE] Navigating to cattle list with refresh flag');
          navigate('/gaushala/cattle', { state: { shouldRefresh: true, refreshSheds: true } });
        }, 3000);
      } else {
        const errorMsg = response.error || 'Failed to create cattle';
        console.error('❌ [ADD CATTLE] Registration failed:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      // Extract meaningful error message from various error formats
      let errorMessage = 'Failed to register cattle. Please try again.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      console.error('Cattle registration error:', error);

      // ============== FALLBACK TO OFFLINE SAVING ==============
      // If the server is unavailable (500 error, network error, etc.), offer to save offline
      const isServerError = errorMessage.includes('500') ||
                           errorMessage.includes('Internal Server Error') ||
                           errorMessage.includes('Network') ||
                           errorMessage.includes('fetch');

      if (isServerError) {
        console.log('📴 [ADD CATTLE] Server unavailable - attempting offline save');
        try {
          // Reconstruct cattleData for offline save
          const cattleDataForOffline: Omit<Cattle, 'id' | 'createdAt' | 'updatedAt' | 'gaushalaId'> = {
            uniqueAnimalId: formData.uniqueAnimalId,
            name: formData.name.trim(),
            breedId: formData.breedId,
            speciesId: formData.speciesId,
            genderId: formData.genderId,
            colorId: formData.colorId,
            dob: formData.ageYears && parseInt(formData.ageYears) > 0 ? calculateDobFromAge(parseInt(formData.ageYears)) : new Date().toISOString(),
            weight: formData.weight ? parseFloat(formData.weight) : undefined,
            height: formData.height ? parseFloat(formData.height) : undefined,
            hornStatus: formData.hornStatus || undefined,
            rfidTagNo: formData.rfidTagNo,
            earTagNo: formData.earTagNumber || undefined,
            microchipNo: formData.microchipNumber || undefined,
            shedNumber: Array.isArray(formData.shedNumber) && formData.shedNumber.length > 0
              ? formData.shedNumber[0]
              : (typeof formData.shedNumber === 'string' ? formData.shedNumber : undefined),
            vaccinationStatus: formData.vaccinationStatus || undefined,
            disabilityStatus: formData.disabilityStatus || undefined,
            dewormingSchedule: formData.dewormingSchedule || undefined,
            medicalHistory: formData.medicalHistory || undefined,
            lastHealthCheckupDate: formData.lastHealthCheckup ? convertToLocalDateTime(formData.lastHealthCheckup) : undefined,
            vetName: formData.veterinarianName || undefined,
            vetContact: formData.veterinarianContact || undefined,
            milkingStatus: formData.milkingStatus || undefined,
            lactationNumber: formData.lactationNumber ? parseInt(formData.lactationNumber) : undefined,
            milkYieldPerDay: formData.milkYieldPerDay ? parseFloat(formData.milkYieldPerDay) : undefined,
            lastCalvingDate: formData.lastCalvingDate ? convertToLocalDateTime(formData.lastCalvingDate) : undefined,
            calvesCount: formData.numberOfCalves ? parseInt(formData.numberOfCalves) : undefined,
            pregnancyStatus: formData.pregnancyStatus || undefined,
            sourceId: formData.sourceOfAcquisition ? parseInt(formData.sourceOfAcquisition) : undefined,
            dateOfAcquisition: formData.dateOfAcquisition ? convertToLocalDateTime(formData.dateOfAcquisition) : undefined,
            previousOwner: formData.previousOwner || undefined,
            ownershipId: formData.ownershipStatus ? parseInt(formData.ownershipStatus) : undefined,
            feedingSchedule: formData.feedingSchedule || undefined,
            feedTypeIds: formData.feedTypeIds && formData.feedTypeIds.length > 0 ? formData.feedTypeIds : undefined,
            dateOfEntry: formData.dateOfEntry ? convertToLocalDateTime(formData.dateOfEntry) : convertToLocalDateTime(new Date().toISOString().split('T')[0]),
            isActive: true,
            totalDungCollected: 0,
            lastDungCollection: 0,
          };

          const hasFiles = formData.photoFile || formData.healthCertificate || formData.vaccinationRecord || formData.purchaseDocument;
          const offlineResult = await offlineCattleApi.create(cattleDataForOffline);

          if (offlineResult.success) {
            console.log('✅ [ADD CATTLE] Cattle saved offline with temp ID:', offlineResult.tempId);
            setMessage({
              type: 'success',
              text: `Server unavailable - Cattle saved locally! ${hasFiles ? 'Documents will upload when server is back.' : ''} Will sync automatically when server reconnects.`
            });

            // Navigate after short delay
            setTimeout(() => {
              navigate('/gaushala/cattle', { state: { shouldRefresh: true } });
            }, 2500);
            return;
          }
        } catch (offlineError) {
          console.error('❌ [ADD CATTLE] Offline save also failed:', offlineError);
        }
      }

      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/cattle');
  };

  const sections = [
    {
      id: 'basic',
      title: 'Basic Identification',
      icon: User,
      color: 'from-blue-500 to-indigo-600',
      progress: 0
    },
    {
      id: 'physical',
      title: 'Physical Characteristics',
      icon: Ruler,
      color: 'from-purple-500 to-violet-600',
      progress: 0
    },
    {
      id: 'health',
      title: 'Health & Medical',
      icon: Activity,
      color: 'from-red-500 to-pink-600',
      progress: 0
    },
    {
      id: 'origin',
      title: 'Origin & Ownership',
      icon: Globe,
      color: 'from-emerald-500 to-teal-600',
      progress: 0
    },
    {
      id: 'shelter',
      title: 'Shelter & Feeding',
      icon: Settings,
      color: 'from-amber-500 to-yellow-600',
      progress: 0
    }
  ];


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back</span>
          </button>

          <div className="h-8 w-px bg-gray-300"></div>

          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🐄</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Register New Cattle</h2>
              <p className="text-gray-600">Complete cattle registration with comprehensive details</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="cattle-form"
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Save className="h-4 w-4" />
            {isLoading ? 'Saving...' : isOnline ? 'Save Cattle' : 'Save Locally'}
          </button>
        </div>
      </div>

      {/* Offline Notice */}
      {!isOnline && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-center gap-3">
            <WifiOff className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">You're currently offline</p>
              <p className="text-sm text-amber-700">
                Cattle data will be saved locally and automatically synced when you reconnect.
                {(formData.photoFile || formData.healthCertificate || formData.vaccinationRecord || formData.purchaseDocument) &&
                  ' Note: Documents will be uploaded after reconnecting.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pending Sync Notice */}
      {pendingSyncCount > 0 && isOnline && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-blue-800">
              <span className="font-medium">{pendingSyncCount} pending change{pendingSyncCount !== 1 ? 's' : ''}</span> waiting to sync
            </p>
            <button
              type="button"
              onClick={triggerSync}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Sync Now
            </button>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between overflow-x-auto">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={section.id} className="flex items-center flex-shrink-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="ml-2">
                  <p className="text-xs font-medium text-gray-700">{section.title}</p>
                </div>
                {index < sections.length - 1 && (
                  <div className="w-8 h-px bg-gray-300 mx-3"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-lg border-l-4 ${
          message.type === 'success'
            ? 'bg-green-50 border-green-400 text-green-800'
            : message.type === 'warning'
            ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
            : 'bg-red-50 border-red-400 text-red-800'
        }`}>
          <div className="flex items-center gap-3">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : message.type === 'warning' ? (
              <Clock className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}
      <form id="cattle-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

          {/* Left Section - 3 columns */}
          <div className="xl:col-span-3 space-y-6">

            {/* Basic Identification */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                🐄 Basic Identification
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Unique Animal ID <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.uniqueAnimalId.length}/{VALIDATION_RULES.uniqueAnimalId.max})</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.uniqueAnimalId}
                        onChange={(e) => {
                          if (e.target.value.length <= VALIDATION_RULES.uniqueAnimalId.max) {
                            handleUniqueAnimalIdChange(e.target.value);
                          }
                        }}
                        placeholder="Enter unique animal ID"
                        maxLength={VALIDATION_RULES.uniqueAnimalId.max}
                        className={`w-full px-4 py-3.5 pr-10 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                          errors.uniqueAnimalId ? 'border-red-300 bg-red-50' :
                          duplicateCheckState.uniqueAnimalId.exists === false ? 'border-green-300' :
                          'border-gray-200'
                        }`}
                        required
                      />
                      {duplicateCheckState.uniqueAnimalId.checking && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                      {duplicateCheckState.uniqueAnimalId.exists === false && !errors.uniqueAnimalId && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                      )}
                    </div>
                    {errors.uniqueAnimalId && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.uniqueAnimalId}
                      </p>
                    )}
                    {duplicateCheckState.uniqueAnimalId.exists === false && !errors.uniqueAnimalId && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        This Unique Animal ID is available
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Name <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.name.length}/{VALIDATION_RULES.name.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.name.max) {
                          handleInputChange('name', e.target.value);
                        }
                      }}
                      placeholder="Enter cattle name"
                      maxLength={VALIDATION_RULES.name.max}
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'cattle-name-error' : undefined}
                      className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                        errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                    />
                    {errors.name && (
                      <p id="cattle-name-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <SelectField
                    label="Breed"
                    value={formData.breedId}
                    onChange={(value: string) => handleInputChange('breedId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading breeds...' : 'Select Breed'}
                    required
                    error={errors.breedId}
                    options={breeds.filter(breed => breed && breed.id && breed.name).map(breed => ({
                      value: breed.id.toString(),
                      label: breed.name || ''
                    })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                  />

                  <SelectField
                    label="Species"
                    value={formData.speciesId}
                    onChange={(value: string) => handleInputChange('speciesId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading species...' : 'Select Species'}
                    required
                    error={errors.speciesId}
                    options={species.filter(s => s && s.id && s.name).map(s => ({
                      value: s.id.toString(),
                      label: s.name || ''
                    })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                  />

                  <SelectField
                    label="Gender"
                    value={formData.genderId}
                    onChange={(value: string) => handleInputChange('genderId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading genders...' : 'Select Gender'}
                    required
                    error={errors.genderId}
                    options={genders.filter(gender => gender && gender.id && gender.name).map(gender => ({
                      value: gender.id.toString(),
                      label: gender.name || ''
                    })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                  />

                  <SelectField
                    label="Color"
                    value={formData.colorId}
                    onChange={(value: string) => handleInputChange('colorId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading colors...' : 'Select Color'}
                    error={errors.colorId}
                    options={colors.filter(color => color && color.id && color.name).map(color => ({
                      value: color.id.toString(),
                      label: color.name || ''
                    })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                  />

                  <InputField
                    label="Age (Years)"
                    value={formData.ageYears}
                    onChange={(value: string) => {
                      const filtered = value.replace(/[^0-9]/g, '');
                      if (filtered === '') {
                        handleInputChange('ageYears', '');
                        return;
                      }
                      const numVal = parseInt(filtered);
                      if (numVal <= 30) {
                        handleInputChange('ageYears', String(numVal));
                      }
                    }}
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter age in years"
                    required
                    error={errors.ageYears}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Date of Entry <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.dateOfEntry}
                      onChange={(e) => {
                        handleInputChange('dateOfEntry', e.target.value);
                      }}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                    <p className="text-xs text-gray-500 mt-1">📅 Select any past or future date</p>
                  </div>
                </div>
              </div>


            {/* Physical Characteristics */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center">
                  <Ruler className="h-5 w-5 text-white" />
                </div>
                📏 Physical Characteristics
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Weight (kg)
                      <span className="text-xs text-gray-500 font-normal ml-2">(Max: 2000 kg)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.weight}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow only numbers and decimal point
                        value = value.replace(/[^0-9.]/g, '');

                        // Ensure only one decimal point
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }

                        // Allow empty string for clearing the field
                        if (value === '') {
                          handleInputChange('weight', '');
                          return;
                        }

                        // Validate length (max 6 characters including decimal)
                        if (value.length > VALIDATION_RULES.weight.maxLength) {
                          return;
                        }

                        // Validate numeric value range
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          if (numValue < VALIDATION_RULES.weight.min || numValue > VALIDATION_RULES.weight.max) {
                            setErrors(prev => ({
                              ...prev,
                              weight: `Weight must be between ${VALIDATION_RULES.weight.min} and ${VALIDATION_RULES.weight.max} kg`
                            }));
                          } else {
                            setErrors(prev => {
                              const { weight, ...rest } = prev;
                              return rest;
                            });
                          }
                        }

                        handleInputChange('weight', value);
                      }}
                      inputMode="decimal"
                      min={VALIDATION_RULES.weight.min}
                      max={VALIDATION_RULES.weight.max}
                      placeholder="Weight in kilograms"
                      className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                        errors.weight ? 'border-red-300 bg-red-50' :
                        formData.weight && parseFloat(formData.weight) >= VALIDATION_RULES.weight.min && parseFloat(formData.weight) <= VALIDATION_RULES.weight.max ? 'border-green-300' :
                        'border-gray-200'
                      }`}
                    />
                    {errors.weight && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.weight}
                      </p>
                    )}
                    {formData.weight && !errors.weight && parseFloat(formData.weight) >= VALIDATION_RULES.weight.min && parseFloat(formData.weight) <= VALIDATION_RULES.weight.max && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        Valid weight
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Height (cm)
                      <span className="text-xs text-gray-500 font-normal ml-2">(Max: 300 cm)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.height}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow only numbers (no decimal point for height)
                        value = value.replace(/[^0-9]/g, '');

                        // Allow empty string for clearing the field
                        if (value === '') {
                          handleInputChange('height', '');
                          return;
                        }

                        // Validate length (max 3 characters)
                        if (value.length > VALIDATION_RULES.height.maxLength) {
                          return;
                        }

                        // Validate numeric value range
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          if (numValue < VALIDATION_RULES.height.min || numValue > VALIDATION_RULES.height.max) {
                            setErrors(prev => ({
                              ...prev,
                              height: `Height must be between ${VALIDATION_RULES.height.min} and ${VALIDATION_RULES.height.max} cm`
                            }));
                          } else {
                            setErrors(prev => {
                              const { height, ...rest } = prev;
                              return rest;
                            });
                          }
                        }

                        handleInputChange('height', value);
                      }}
                      inputMode="numeric"
                      min={VALIDATION_RULES.height.min}
                      max={VALIDATION_RULES.height.max}
                      placeholder="Height in centimeters"
                      className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                        errors.height ? 'border-red-300 bg-red-50' :
                        formData.height && parseFloat(formData.height) >= VALIDATION_RULES.height.min && parseFloat(formData.height) <= VALIDATION_RULES.height.max ? 'border-green-300' :
                        'border-gray-200'
                      }`}
                    />
                    {errors.height && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.height}
                      </p>
                    )}
                    {formData.height && !errors.height && parseFloat(formData.height) >= VALIDATION_RULES.height.min && parseFloat(formData.height) <= VALIDATION_RULES.height.max && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        Valid height
                      </p>
                    )}
                  </div>

                  <SelectField
                    label="Horn Status"
                    value={formData.hornStatus}
                    onChange={(value: string) => handleInputChange('hornStatus', value)}
                    placeholder={masterDataLoading ? 'Loading statuses...' : ownerships.length === 0 ? 'No statuses available' : 'Select Status'}
                    options={[
                      { value: 'horned', label: 'Horned' },
                      { value: 'dehorned', label: 'Dehorned' },
                      { value: 'polled', label: 'Polled (Naturally hornless)' }
                    ].sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      RFID Tag Number <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.rfidTagNo.length}/{VALIDATION_RULES.rfidTagNo.max})</span>
                    </label>
                    <div className="flex gap-3">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={formData.rfidTagNo}
                          onChange={(e) => {
                            if (e.target.value.length <= VALIDATION_RULES.rfidTagNo.max) {
                              handleRfidTagChange(e.target.value);
                            }
                          }}
                          placeholder="Scan or enter RFID tag"
                          maxLength={VALIDATION_RULES.rfidTagNo.max}
                          className={`w-full px-4 py-3.5 pr-10 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                            errors.rfidTagNo ? 'border-red-300 bg-red-50' :
                            duplicateCheckState.rfidTagNo.exists === false ? 'border-green-300' :
                            'border-gray-200'
                          }`}
                          required
                        />
                        {duplicateCheckState.rfidTagNo.checking && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          </div>
                        )}
                        {duplicateCheckState.rfidTagNo.exists === false && !errors.rfidTagNo && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleScanRfid}
                        disabled={isScanning}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        <Scan className="h-5 w-5" />
                        {isScanning ? 'Scanning...' : 'Scan'}
                      </button>
                    </div>
                    {errors.rfidTagNo && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.rfidTagNo}
                      </p>
                    )}
                    {duplicateCheckState.rfidTagNo.exists === false && !errors.rfidTagNo && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        This RFID Tag is available
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Ear Tag Number
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.earTagNumber.length}/{VALIDATION_RULES.earTagNumber.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.earTagNumber}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.earTagNumber.max) {
                          handleInputChange('earTagNumber', e.target.value);
                        }
                      }}
                      placeholder="Ear tag number"
                      maxLength={VALIDATION_RULES.earTagNumber.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Microchip Number
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.microchipNumber.length}/{VALIDATION_RULES.microchipNumber.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.microchipNumber}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.microchipNumber.max) {
                          handleInputChange('microchipNumber', e.target.value);
                        }
                      }}
                      placeholder="Microchip number"
                      maxLength={VALIDATION_RULES.microchipNumber.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>
                </div>
              </div>

            {/* Health & Medical Records */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                🩺 Health & Medical Records
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Vaccination Status
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.vaccinationStatus.length}/{VALIDATION_RULES.vaccinationStatus.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.vaccinationStatus}
                      onChange={(e) => {
                        const alphabetsOnly = e.target.value.replace(/[^a-zA-Z\s,.-]/g, '');
                        if (alphabetsOnly.length <= VALIDATION_RULES.vaccinationStatus.max) {
                          handleInputChange('vaccinationStatus', alphabetsOnly);
                        }
                      }}
                      placeholder="e.g., FMD, HS completed"
                      maxLength={VALIDATION_RULES.vaccinationStatus.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <SelectField
                    label="Health Status"
                    value={formData.disabilityStatus}
                    onChange={(value: string) => handleInputChange('disabilityStatus', value)}
                    placeholder="Select health status"
                    required={true}
                    error={errors.disabilityStatus}
                    options={[
                      { value: 'HEALTHY', label: 'Healthy' },
                      { value: 'SICK', label: 'Sick' },
                      { value: 'UNDER_TREATMENT', label: 'Under Treatment' }
                    ].sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Veterinarian Name
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.veterinarianName.length}/{VALIDATION_RULES.veterinarianName.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.veterinarianName}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z\s.]/g, '');
                        if (value.length <= VALIDATION_RULES.veterinarianName.max) {
                          handleInputChange('veterinarianName', value);
                        }
                      }}
                      placeholder="Name of attending veterinarian (alphabets only)"
                      maxLength={VALIDATION_RULES.veterinarianName.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Veterinarian Contact
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.veterinarianContact.length}/{VALIDATION_RULES.veterinarianContact.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.veterinarianContact}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= VALIDATION_RULES.veterinarianContact.max) {
                          handleInputChange('veterinarianContact', value);
                        }
                      }}
                      placeholder="Phone number"
                      maxLength={VALIDATION_RULES.veterinarianContact.max}
                      inputMode="numeric"
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <InputField
                    label="Last Health Check-up"
                    value={formData.lastHealthCheckup}
                    onChange={(value: string) => handleInputChange('lastHealthCheckup', value)}
                    type="date"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Deworming Schedule
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.dewormingSchedule.length}/{VALIDATION_RULES.dewormingSchedule.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.dewormingSchedule}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.dewormingSchedule.max) {
                          handleInputChange('dewormingSchedule', e.target.value);
                        }
                      }}
                      placeholder="e.g., Every 6 months"
                      maxLength={VALIDATION_RULES.dewormingSchedule.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div className="lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Medical History
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.medicalHistory.length}/{VALIDATION_RULES.medicalHistory.max})</span>
                    </label>
                    <textarea
                      value={formData.medicalHistory}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.medicalHistory.max) {
                          handleInputChange('medicalHistory', e.target.value);
                        }
                      }}
                      placeholder="Previous medical treatments, surgeries, etc."
                      maxLength={VALIDATION_RULES.medicalHistory.max}
                      rows={4}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Sections Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Reproductive Details (Female only) */}
                {/* Note: Check if genderId matches female gender ID from master data */}
                {genders.find(g => g.name && g.name.toLowerCase() === 'female' && g.id === formData.genderId) && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                        <Baby className="h-5 w-5 text-white" />
                      </div>
                      🤱 Reproductive Details
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <SelectField
                        label="Milking Status"
                        value={formData.milkingStatus}
                        onChange={(value: string) => handleInputChange('milkingStatus', value)}
                        placeholder={masterDataLoading ? 'Loading statuses...' : ownerships.length === 0 ? 'No statuses available' : 'Select Status'}
                        options={[
                          { value: 'lactating', label: 'Lactating' },
                          { value: 'dry', label: 'Dry' },
                          { value: 'pregnant', label: 'Pregnant' },
                          { value: 'not_breeding', label: 'Not for Breeding' }
                        ].sort((a, b) => a.label.localeCompare(b.label))}
                      />

                      <InputField
                        label="Milk Yield Per Day (Liters)"
                        value={formData.milkYieldPerDay}
                        onChange={(value: string) => handleInputChange('milkYieldPerDay', value)}
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder="Daily milk yield"
                      />

                      <InputField
                        label="Number of Calves"
                        value={formData.numberOfCalves}
                        onChange={(value: string) => handleInputChange('numberOfCalves', value)}
                        type="number"
                        min="0"
                        max="50"
                        placeholder="Total calves born"
                      />

                      <InputField
                        label="Lactation Number"
                        value={formData.lactationNumber}
                        onChange={(value: string) => handleInputChange('lactationNumber', value)}
                        type="number"
                        min="0"
                        max="20"
                        placeholder="Current lactation number"
                      />

                      <InputField
                        label="Last Calving Date"
                        value={formData.lastCalvingDate}
                        onChange={(value: string) => handleInputChange('lastCalvingDate', value)}
                        type="date"
                      />

                      <SelectField
                        label="Pregnancy Status"
                        value={formData.pregnancyStatus}
                        onChange={(value: string) => handleInputChange('pregnancyStatus', value)}
                        placeholder={masterDataLoading ? 'Loading statuses...' : ownerships.length === 0 ? 'No statuses available' : 'Select Status'}
                        options={[
                          { value: 'pregnant', label: 'Pregnant' },
                          { value: 'not_pregnant', label: 'Not Pregnant' },
                          { value: 'unknown', label: 'Unknown' }
                        ].sort((a, b) => a.label.localeCompare(b.label))}
                      />
                    </div>
                  </div>
                )}

                {/* Origin & Ownership */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                      <Globe className="h-5 w-5 text-white" />
                    </div>
                    🌍 Origin & Ownership
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SelectField
                      label="Source of Acquisition"
                      value={formData.sourceOfAcquisition}
                      onChange={(value: string) => handleInputChange('sourceOfAcquisition', value)}
                      placeholder={masterDataLoading ? 'Loading sources...' : sources.length === 0 ? 'No sources available' : 'Select Source'}
                      options={sources.map(source => ({
                        value: String(source.id),
                        label: source.name || ''
                      })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">
                        Previous Owner
                        <span className="text-xs text-gray-500 font-normal ml-2">({formData.previousOwner.length}/{VALIDATION_RULES.previousOwner.max})</span>
                      </label>
                      <input
                        type="text"
                        value={formData.previousOwner}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^a-zA-Z\s.]/g, '');
                          if (value.length <= VALIDATION_RULES.previousOwner.max) {
                            handleInputChange('previousOwner', value);
                          }
                        }}
                        placeholder="Name of previous owner (alphabets only)"
                        maxLength={VALIDATION_RULES.previousOwner.max}
                        className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                      />
                    </div>

                    <InputField
                      label="Date of Acquisition"
                      value={formData.dateOfAcquisition}
                      onChange={(value: string) => handleInputChange('dateOfAcquisition', value)}
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                    />

                    <SelectField
                      label="Ownership Status"
                      value={formData.ownershipStatus}
                      onChange={(value: string) => handleInputChange('ownershipStatus', value)}
                      placeholder={masterDataLoading ? 'Loading statuses...' : ownerships.length === 0 ? 'No statuses available' : 'Select Status'}
                      options={ownerships.map(ownership => ({
                        value: String(ownership.id),
                        label: ownership.name || ''
                      })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                    />
                  </div>
                </div>

                {/* Shelter & Feeding */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center">
                      <Settings className="h-5 w-5 text-white" />
                    </div>
                    🏠 Shelter & Feeding
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <MultiSelectField
                      label="Shed Assignment"
                      value={formData.shedNumber}
                      onChange={(value: any) => handleInputChange('shedNumber', value)}
                      placeholder={masterDataLoading ? 'Loading sheds...' : sheds.length === 0 ? 'No sheds available' : 'Select Sheds (multiple)'}
                      options={sheds.map(shed => ({
                        value: shed.shedNumber,
                        label: `${shed.shedName} (${shed.currentOccupancy || 0}/${shed.capacity || 0} occupied)`
                      })).sort((a, b) => a.label.localeCompare(b.label))}
                    />

                    <MultiSelectField
                      label="Type of Feed"
                      value={formData.feedTypeIds.map(id => String(id))}
                      onChange={(values: string[]) => {
                        // Convert string values back to numbers for feedTypeIds
                        const numericIds = values.map(v => parseInt(v)).filter(n => !isNaN(n));
                        handleInputChange('feedTypeIds', numericIds);
                      }}
                      placeholder={masterDataLoading ? 'Loading feed types...' : feedTypes.length === 0 ? 'No feed types available' : 'Select Feed Types (multiple)'}
                      options={feedTypes.map(feedType => ({
                        value: String(feedType.id),
                        label: feedType.name || ''
                      })).sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                    />

                    <div className="md:col-span-2">
                      <TextAreaField
                        label="Feeding Schedule"
                        value={formData.feedingSchedule}
                        onChange={(value: string) => handleInputChange('feedingSchedule', value)}
                        placeholder="Feeding times and quantities"
                        maxLength={300}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {/* Right Sidebar */}
          <div className="xl:col-span-1 space-y-6">

            {/* Supporting Documents */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-slate-500 to-gray-600 rounded-full flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                📎 Documents
              </h2>

                <div className="space-y-6">
                  <FileUploadField
                    label="Photo"
                    value={formData.photoFile}
                    onChange={(value: File | null) => handleInputChange('photoFile', value)}
                    accept="image/*"
                  />

                  <FileUploadField
                    label="Health Certificate"
                    value={formData.healthCertificate}
                    onChange={(value: File | null) => handleInputChange('healthCertificate', value)}
                    accept=".pdf"
                  />

                  <FileUploadField
                    label="Vaccination Record"
                    value={formData.vaccinationRecord}
                    onChange={(value: File | null) => handleInputChange('vaccinationRecord', value)}
                    accept=".pdf"
                  />

                  <FileUploadField
                    label="Purchase/Donation Document"
                    value={formData.purchaseDocument}
                    onChange={(value: File | null) => handleInputChange('purchaseDocument', value)}
                    accept=".pdf"
                  />
                </div>

                {/* Quick Stats */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Form Progress</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Required Fields</span>
                      <span className="font-medium text-gray-900">
                        {Object.keys(errors).length === 0 ? '✓ Complete' : `${Object.keys(errors).length} missing`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Documents</span>
                      <span className="font-medium text-gray-900">
                        {[formData.photoFile, formData.healthCertificate, formData.vaccinationRecord, formData.purchaseDocument].filter(Boolean).length}/4
                      </span>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}